import useAuth from "@/components/auth/useAuth"
import useSnackbar from "@/helpers/add-snackbar"
import { Button } from "@material-ui/core"
import { FileCopyOutlined } from "@material-ui/icons"
import React from "react"
import { useHistory } from "react-router-dom"

/**
 * CopyURLButton
 *
 * A reusable button that copies the current page URL to the clipboard.
 *
 * ✅ Common use cases:
 *   - Admin pages where you need to share links to a specific filtered or detailed view.
 *   - Debugging / QA flows where developers or testers need to copy URLs quickly.
 *
 * 🔐 Permissions:
 *   - By default, this button is **restricted to superusers only** (`isSuperUserOnly = true`).
 *   - Pass `isSuperUserOnly={false}` to make it available to all logged-in users.
 *
 * 🎨 Styling:
 *   - Superuser-only buttons get a brown background to visually distinguish them.
 *
 * 📱 UX:
 *   - When clicked, the button copies the full current URL (`origin + pathname + query`) to the clipboard.
 *   - A `Snackbar` notification confirms success.
 *
 * @param color - the color of the button (default: "primary").
 * @param variant - the variant of the button (default: "contained").
 * @param isSuperUserOnly - whether the button should only be visible to superusers (default: false).
 */
const CopyURLButton = ({
  color = "primary",
  variant = "contained",
  isSuperUserOnly = false
}: {
  color?: "inherit" | "primary" | "secondary"
  isSuperUserOnly?: boolean
  variant?: "contained" | "outlined" | "text"
}) => {
  const history = useHistory()
  const { user } = useAuth()
  const { addSnackbar } = useSnackbar()

  const handleCopy = () => {
    const url = `${window.location.origin}${history.location.pathname}${history.location.search}`

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          addSnackbar("URL copied to clipboard: " + url, "success")
        })
        .catch(err => {
          addSnackbar("Failed to copy: " + err, "error")
        })
    } else {
      // fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = url
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand("copy")
      document.body.removeChild(textArea)
      addSnackbar("URL copied to clipboard: " + url, "success")
    }
  }

  const buttonStyle = isSuperUserOnly ? { backgroundColor: "brown" } : {}

  return !user?.superuser && isSuperUserOnly ? (
    <></>
  ) : (
    <>
      <Button
        variant={variant}
        color={color}
        onClick={handleCopy}
        size="small"
        style={buttonStyle}
        startIcon={<FileCopyOutlined />}
      >
        URL
      </Button>
    </>
  )
}

export default CopyURLButton
